import React, { useEffect, useState } from "react";

import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { Box, Card, Collapse, Stack, Tab, Tabs, Tooltip } from "@mui/material";

import { ButtonVariant } from "Constant/button";
import { useParams } from "react-router-dom";
import { ClipLoader } from "react-spinners";

import { retryFailedLeads } from "helpers/leads/reports";
import { renderResponseMessage } from "helpers/leads/responseMessageRenderer";

import StandartButton from "components/atoms/StandartButton";
import { GenericModal } from "components/GenericModal";
import TablePagination from "components/organisms/GenericTable/TablePagination";

import {
    FailedLead,
    FailedLeadsModalProps,
    FailedLeadsTab,
    getLeadResponseBody,
    RetryFailedLeadsPayload,
} from "./FailedLeadsModal.types";

const PAGE_SIZE_OPTIONS = [5, 10, 25, 50, 100];
const DEFAULT_PAGE = 1;
const DEFAULT_PAGE_SIZE = 10;

const FailedLeadsModal = ({
    destination,
    onClose,
    failedLeads,
    duplicateLeads,
    handleGetDuplicateLeads,
    handleGetFailedLeads,
    isLoadingFailedLeads,
    failedLeadsPagination,
    duplicateLeadsPagination,
    setFailedLeadsPagination,
    setDuplicateLeadsPagination,
    firstActiveTab,
}: FailedLeadsModalProps) => {
    const params = useParams();
    const id = params?.id;
    const [loadingFailedLeads, setLoadingFailedLeads] = useState<number[]>([]);
    const [selectedLeadIds, setSelectedLeadIds] = useState<number[]>([]);
    const [activeTab, setActiveTab] = useState<FailedLeadsTab>(
        firstActiveTab ?? FailedLeadsTab.Failed,
    );
    const [expandedReasons, setExpandedReasons] = useState<number[]>([]);

    const toggleExpandReason = (statId: number) => {
        setExpandedReasons((prev) =>
            prev.includes(statId)
                ? prev.filter((id) => id !== statId)
                : [...prev, statId],
        );
    };

    const resetPagination = (
        setter: FailedLeadsModalProps["setFailedLeadsPagination"],
    ) => {
        setter((prev) => ({
            ...prev,
            currentPage: DEFAULT_PAGE,
            pageSize: DEFAULT_PAGE_SIZE,
        }));
    };

    const handleClose = () => {
        resetPagination(setFailedLeadsPagination);
        resetPagination(setDuplicateLeadsPagination);
        handleGetFailedLeads(DEFAULT_PAGE, DEFAULT_PAGE_SIZE);
        handleGetDuplicateLeads(DEFAULT_PAGE, DEFAULT_PAGE_SIZE);
        onClose();
    };

    const handlePageChange = (page: number) => {
        activeTab === FailedLeadsTab.Failed
            ? handleGetFailedLeads(page, failedLeadsPagination.pageSize)
            : handleGetDuplicateLeads(page, duplicateLeadsPagination.pageSize);
    };

    const handlePageSizeChange = (newPageSize: number) => {
        activeTab === FailedLeadsTab.Failed
            ? handleGetFailedLeads(1, newPageSize)
            : handleGetDuplicateLeads(1, newPageSize);
    };

    const toggleSelectLead = (statId: number) => {
        setSelectedLeadIds((prev) =>
            prev.includes(statId)
                ? prev.filter((leadId) => leadId !== statId)
                : [...prev, statId],
        );
    };

    const selectableLeadIds = (failedLeads ?? [])
        .filter((lead) => lead.retry_available && !lead.scheduled)
        .map((lead) => lead.stat_id);

    const isAllSelectable = selectableLeadIds.length > 0;

    const handleSelectAll = () => {
        const selectableIds = selectableLeadIds;

        setSelectedLeadIds((prev) =>
            prev.length === selectableIds.length ? [] : selectableIds,
        );
    };

    const handleRetryFailedLead = async (statID?: number) => {
        let payload: RetryFailedLeadsPayload;
        const targetIds = statID ? [statID] : selectedLeadIds;

        if (statID) {
            payload = { stat_ids: [statID] };
        } else if (selectedLeadIds.length > 0) {
            payload = { stat_ids: selectedLeadIds };
        } else {
            // Retry all available leads
            if (!isAllSelectable) return;
            payload = { all_stats: true };
        }

        setLoadingFailedLeads(targetIds);

        const response = await retryFailedLeads(id, payload);
        if (response.status) {
            handleGetFailedLeads(
                failedLeadsPagination.currentPage,
                failedLeadsPagination.pageSize,
            );
        }

        // Clear selection after retry
        setSelectedLeadIds([]);
        setLoadingFailedLeads([]);
    };

    useEffect(() => {
        setSelectedLeadIds([]);
        setExpandedReasons([]);
    }, [activeTab]);

    const renderLeadsList = (leads: FailedLead[], isDuplicate = false) => {
        return (leads ?? []).map((lead, index) => {
            const responseBody = getLeadResponseBody(lead.last_response);

            return (
                <Card
                    key={`${lead.stat_id} - ${index}`}
                    variant="outlined"
                    style={{
                        padding: "1rem",
                        cursor: isDuplicate
                            ? "default"
                            : !lead.retry_available || lead.scheduled
                              ? "default"
                              : "pointer",
                    }}
                    onClick={() => {
                        if (
                            !isDuplicate &&
                            lead.retry_available &&
                            !lead.scheduled
                        ) {
                            toggleSelectLead(lead.stat_id);
                        }
                    }}
                >
                    <Stack
                        direction="row"
                        justifyContent="space-between"
                        alignItems="center"
                        width="100%"
                    >
                        <Stack
                            direction="row"
                            gap={2}
                            alignItems="start"
                            width="100%"
                        >
                            {!isDuplicate && (
                                <Box>
                                    <div
                                        style={{
                                            width: "18px",
                                            height: "18px",
                                            border: "2px solid #ccc",
                                            borderRadius: "3px",
                                            backgroundColor:
                                                selectedLeadIds.includes(
                                                    lead.stat_id,
                                                )
                                                    ? "#007bff"
                                                    : "transparent",
                                            cursor:
                                                !lead.retry_available ||
                                                lead.scheduled
                                                    ? "not-allowed"
                                                    : "pointer",
                                            opacity:
                                                !lead.retry_available ||
                                                lead.scheduled
                                                    ? 0.5
                                                    : 1,
                                            display: "flex",
                                            alignItems: "center",
                                            justifyContent: "center",
                                            position: "relative",
                                            transition: "all 0.2s ease",
                                        }}
                                        onClick={(e) => {
                                            e.preventDefault();
                                            e.stopPropagation();
                                            if (
                                                lead.retry_available &&
                                                !lead.scheduled
                                            ) {
                                                toggleSelectLead(lead.stat_id);
                                            }
                                        }}
                                    >
                                        {selectedLeadIds.includes(
                                            lead.stat_id,
                                        ) && (
                                            <div
                                                style={{
                                                    width: "6px",
                                                    height: "10px",
                                                    border: "2px solid white",
                                                    borderTop: "none",
                                                    borderLeft: "none",
                                                    transform: "rotate(45deg)",
                                                    marginTop: "-1px",
                                                }}
                                            />
                                        )}
                                    </div>
                                </Box>
                            )}

                            <Stack width="100%">
                                <Box fontSize="14px" fontWeight={700}>
                                    Source:{" "}
                                    <Box component="span" fontWeight={400}>
                                        {lead.lead_type}
                                    </Box>
                                </Box>
                                <Box fontSize="14px" fontWeight={700}>
                                    Destination:{" "}
                                    <Box component="span" fontWeight={400}>
                                        {destination}
                                    </Box>
                                </Box>
                                <Box fontSize="14px" fontWeight={700}>
                                    Total Retries:{" "}
                                    <Box component="span" fontWeight={400}>
                                        {lead.total_retries}
                                    </Box>
                                </Box>
                                <Box fontSize="14px" fontWeight={700}>
                                    Last Retried At:{" "}
                                    <Box component="span" fontWeight={400}>
                                        {lead.last_retried_at}
                                    </Box>
                                </Box>
                                <Box fontSize="14px" fontWeight={700}>
                                    Last Status:{" "}
                                    <Box component="span" fontWeight={400}>
                                        {lead.last_status}
                                    </Box>
                                </Box>

                                {responseBody?.code && (
                                    <Box fontSize="14px" fontWeight={700}>
                                        Response Status:{" "}
                                        <Box component="span" fontWeight={400}>
                                            {responseBody.code}
                                        </Box>
                                    </Box>
                                )}

                                {renderResponseMessage(lead.last_response) && (
                                    <Box
                                        mt={1}
                                        onClick={(e) => e.stopPropagation()}
                                    >
                                        <Box
                                            fontSize="14px"
                                            sx={{
                                                cursor: "pointer",
                                                display: "flex",
                                                alignItems: "center",
                                                gap: "4px",
                                                userSelect: "none",
                                            }}
                                            onClick={() =>
                                                toggleExpandReason(lead.stat_id)
                                            }
                                        >
                                            <Box
                                                fontWeight={700}
                                                color="#DC4200"
                                            >
                                                Reason:
                                            </Box>
                                            {!expandedReasons.includes(
                                                lead.stat_id,
                                            ) && (
                                                <Box
                                                    component="span"
                                                    fontWeight={400}
                                                    color="#333"
                                                    sx={{
                                                        overflow: "hidden",
                                                        textOverflow:
                                                            "ellipsis",
                                                        whiteSpace: "nowrap",
                                                        maxWidth: "200px",
                                                        fontFamily: (() => {
                                                            const msg =
                                                                renderResponseMessage(
                                                                    lead.last_response,
                                                                );
                                                            if (
                                                                typeof msg !==
                                                                "string"
                                                            ) {
                                                                return "inherit";
                                                            }
                                                            return msg.startsWith(
                                                                "{",
                                                            ) ||
                                                                msg.startsWith(
                                                                    "[",
                                                                )
                                                                ? "monospace"
                                                                : "inherit";
                                                        })(),
                                                    }}
                                                >
                                                    {(() => {
                                                        const msg =
                                                            renderResponseMessage(
                                                                lead.last_response,
                                                            );
                                                        if (
                                                            typeof msg ===
                                                                "string" &&
                                                            msg.length > 30
                                                        ) {
                                                            return (
                                                                msg.substring(
                                                                    0,
                                                                    30,
                                                                ) + "..."
                                                            );
                                                        }
                                                        return msg;
                                                    })()}
                                                </Box>
                                            )}
                                            <Box
                                                component="span"
                                                sx={{
                                                    display: "flex",
                                                    alignItems: "center",
                                                    color: "#DC4200",
                                                    fontSize: "12px",
                                                    fontWeight: 500,
                                                }}
                                            >
                                                {expandedReasons.includes(
                                                    lead.stat_id,
                                                ) ? (
                                                    <>
                                                        <ExpandLessIcon
                                                            sx={{
                                                                fontSize: 18,
                                                            }}
                                                        />
                                                        Collapse
                                                    </>
                                                ) : (
                                                    <>
                                                        <ExpandMoreIcon
                                                            sx={{
                                                                fontSize: 18,
                                                            }}
                                                        />
                                                        Expand
                                                    </>
                                                )}
                                            </Box>
                                        </Box>
                                        <Collapse
                                            in={expandedReasons.includes(
                                                lead.stat_id,
                                            )}
                                        >
                                            <Box
                                                component="pre"
                                                sx={{
                                                    margin: 0,
                                                    marginTop: 1,
                                                    padding: 1.5,
                                                    backgroundColor: "#f5f5f5",
                                                    borderRadius: 1,
                                                    whiteSpace: "pre-wrap",
                                                    wordBreak: "break-word",
                                                    fontFamily: "monospace",
                                                    fontSize: "12px",
                                                    color: "#333",
                                                    border: "1px solid #e0e0e0",
                                                }}
                                            >
                                                {renderResponseMessage(
                                                    lead.last_response,
                                                )}
                                            </Box>
                                        </Collapse>
                                    </Box>
                                )}
                            </Stack>
                        </Stack>

                        {!isDuplicate && (
                            <Box className="position-relative" ml={2}>
                                {loadingFailedLeads.includes(lead.stat_id) && (
                                    <div className="circle-loader max-height-45">
                                        <ClipLoader color={"#000"} size={17} />
                                    </div>
                                )}
                                <Tooltip
                                    title={
                                        lead?.scheduled &&
                                        "Lead retry process is scheduled."
                                    }
                                >
                                    <Box>
                                        <StandartButton
                                            onClick={(e) => {
                                                handleRetryFailedLead(
                                                    lead.stat_id,
                                                );
                                                e?.stopPropagation();
                                            }}
                                            disabled={lead?.scheduled}
                                            variant={
                                                ButtonVariant.BLACK_OUTLINED
                                            }
                                        >
                                            {lead?.scheduled
                                                ? "Scheduled"
                                                : "Retry"}
                                        </StandartButton>
                                    </Box>
                                </Tooltip>
                            </Box>
                        )}
                    </Stack>
                </Card>
            );
        });
    };

    const activePagination =
        activeTab === FailedLeadsTab.Failed
            ? failedLeadsPagination
            : duplicateLeadsPagination;

    return (
        <GenericModal
            open
            onClose={handleClose}
            title="Failed Leads"
            subtitle={destination}
            maxWidth="md"
            content={
                <Box position="relative">
                    {isLoadingFailedLeads && (
                        <Box
                            sx={{
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                position: "absolute",
                                inset: 0,
                                zIndex: 2,
                                background: "rgba(255,255,255,.8)",
                            }}
                        >
                            <ClipLoader
                                color={"#aaa"}
                                loading={true}
                                size={40}
                            />
                        </Box>
                    )}

                    <Stack gap={1} width="100%" px={3} pt={1} pb={2}>
                        <Tabs
                            value={activeTab}
                            onChange={(_event, newValue: FailedLeadsTab) =>
                                setActiveTab(newValue)
                            }
                            sx={{ borderBottom: 1, borderColor: "divider" }}
                        >
                            <Tab
                                label={`Failed Leads (${
                                    failedLeadsPagination?.total || 0
                                })`}
                                sx={{ textTransform: "none" }}
                            />
                            <Tab
                                label={`Duplicate Leads (${
                                    duplicateLeadsPagination?.total || 0
                                })`}
                                sx={{ textTransform: "none" }}
                            />
                        </Tabs>

                        {activeTab === FailedLeadsTab.Failed && (
                            <Stack gap={1} width="100%">
                                <Stack
                                    direction="row"
                                    justifyContent="flex-end"
                                    width="100%"
                                >
                                    <Stack direction="row" gap={1}>
                                        <StandartButton
                                            variant={
                                                ButtonVariant.BLACK_OUTLINED
                                            }
                                            onClick={handleSelectAll}
                                            disabled={!isAllSelectable}
                                            style={{
                                                whiteSpace: "nowrap",
                                            }}
                                        >
                                            {selectedLeadIds.length ===
                                            selectableLeadIds.length
                                                ? "Unselect All"
                                                : "Select All"}
                                        </StandartButton>

                                        <StandartButton
                                            variant={
                                                ButtonVariant.BLACK_OUTLINED
                                            }
                                            onClick={() =>
                                                handleRetryFailedLead()
                                            }
                                            style={{
                                                whiteSpace: "nowrap",
                                            }}
                                            disabled={
                                                loadingFailedLeads.length ===
                                                    0 &&
                                                selectedLeadIds.length === 0
                                            }
                                        >
                                            Retry Selected
                                            <Box
                                                style={{
                                                    background: "#ecf0f1",
                                                    padding: "0 4px",
                                                    borderRadius: "4px",
                                                    lineHeight: "20px",
                                                    marginLeft: 8,
                                                }}
                                            >
                                                {selectedLeadIds.length}
                                            </Box>
                                        </StandartButton>
                                    </Stack>
                                </Stack>

                                {renderLeadsList(failedLeads, false)}
                            </Stack>
                        )}

                        {activeTab === FailedLeadsTab.Duplicate && (
                            <Stack gap={1} width="100%">
                                {renderLeadsList(duplicateLeads, true)}
                            </Stack>
                        )}
                    </Stack>

                    {activePagination?.total > 0 && (
                        <TablePagination
                            currentPage={activePagination.currentPage}
                            totalPages={activePagination.lastPage}
                            totalItems={activePagination.total}
                            pageSize={activePagination.pageSize}
                            onPageChange={handlePageChange}
                            onPageSizeChange={handlePageSizeChange}
                            pageSizeOptions={PAGE_SIZE_OPTIONS}
                        />
                    )}
                </Box>
            }
        />
    );
};

export default FailedLeadsModal;
